// Auth context for the dashboard — wraps useCurrentUser() in a Provider so
// any component can read the active user via useAuth().
//
// Modelled on the role-aware approach in the reference's lib/route-guard.tsx,
// but provider-shaped so it composes with other context consumers (theme,
// react-query).
import { createContext, useContext, type ReactNode } from 'react';
import { useCurrentUser } from '../hooks/useApi';
import type { CurrentUser } from './api';

interface AuthContextValue {
  user: CurrentUser | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, isError, error } = useCurrentUser();
  const value: AuthContextValue = {
    user: data ?? null,
    isLoading,
    isError,
    error: (error as Error) ?? null,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}
